import React from 'react'
import { Text, Heading, Avatar, AvatarImage, AvatarFallback } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import type { Media, Theme } from '../../types/props.js'

const FIX_ITEMS_LENGTH = 9
const COUNT = 3

/**
 * 评论项数据结构
 */
export interface ReviewItem {
  /** 评论者名称 */
  name: string
  /** 评论者头像 URL */
  avatar: Media
  /** 评论内容 */
  comment: string
  /** 评分(1-5星) */
  rating?: number
}

/**
 * 轮播评论数据结构
 */
export interface MarqueeReviewData {
  /** 主标题 */
  title: string
  /** 副标题 */
  subtitle: string
  /** 评论列表 */
  items: ReviewItem[]
  /** 主题：light 或 dark */
  theme?: Theme
}

/**
 * MarqueeReview 组件 Props
 */
export interface MarqueeReviewProps extends React.HTMLAttributes<HTMLElement> {
  /** 评论数据 */
  data: MarqueeReviewData
}

const _chunk = <T,>(array: T[], size: number): T[][] => {
  return Array.from({ length: Math.ceil(array.length / size) }, (_, index) =>
    array.slice(index * size, (index + 1) * size)
  )
}

const StarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.32745 1.36274C7.60256 0.805304 8.39744 0.805305 8.67255 1.36274L10.1766 4.41035C10.2859 4.63171 10.4971 4.78514 10.7413 4.82063L14.1046 5.30934C14.7197 5.39873 14.9654 6.15471 14.5202 6.58861L12.0866 8.96084C11.9098 9.13315 11.8292 9.3814 11.8709 9.62469L12.4454 12.9743C12.5505 13.587 11.9074 14.0542 11.3572 13.765L8.349 12.1835C8.13051 12.0686 7.86949 12.0686 7.65099 12.1835L4.64282 13.765C4.0926 14.0542 3.44953 13.587 3.55461 12.9743L4.12912 9.62469C4.17085 9.3814 4.09019 9.13315 3.91342 8.96084L1.47976 6.58861C1.03462 6.15471 1.28025 5.39873 1.89542 5.30934L5.25866 4.82063C5.50294 4.78514 5.71412 4.63171 5.82336 4.41035L7.32745 1.36274Z"
      fill="#F77234"
    />
  </svg>
)

const unStarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none">
    <path
      d="M6.07501 0.418036C6.35012 -0.139398 7.145 -0.139397 7.42011 0.418037L8.9242 3.46565C9.03344 3.68701 9.24462 3.84043 9.4889 3.87593L12.8521 4.36464C13.4673 4.45403 13.7129 5.21 13.2678 5.64391L10.8341 8.01614C10.6574 8.18844 10.5767 8.43669 10.6184 8.67999L11.1929 12.0296C11.298 12.6423 10.655 13.1095 10.1047 12.8203L7.09656 11.2388C6.87807 11.1239 6.61705 11.1239 6.39855 11.2388L3.39038 12.8203C2.84016 13.1095 2.19709 12.6423 2.30217 12.0296L2.87668 8.67999C2.91841 8.43669 2.83775 8.18844 2.66098 8.01614L0.227318 5.64391C-0.21782 5.21 0.0278133 4.45403 0.642979 4.36464L4.00622 3.87593C4.2505 3.84043 4.46167 3.68701 4.57092 3.46565L6.07501 0.418036Z"
      fill="#75787F"
    />
  </svg>
)

interface CardProps {
  item: ReviewItem
}

const Card = ({ item }: CardProps) => {
  return (
    <div className="rounded-box bg-container-primary text-info-primary min-l:p-[24px] laptop:h-[240px] desktop:h-[240px] lg-desktop:h-[240px] flex h-[240px] grow flex-col justify-between p-[16px]">
      <div className="flex justify-between">
        <div className="flex flex-col">
          <Text size={4} html={item.name} className="laptop:block hidden" />
          <Text size={2} html={item.name} className="laptop:hidden block" />
          <div className="mt-2 flex gap-1">
            {new Array(item.rating || 5).fill(0).map((_, index) => (
              <StarIcon key={index} />
            ))}
            {new Array(5 - (item.rating || 5)).fill(0).map((_, index) => (
              <span key={index}>{unStarIcon()}</span>
            ))}
          </div>
        </div>
        <Avatar size="small" className=" laptop:size-[48px]  size-[40px]">
          <AvatarImage src={item.avatar?.url} alt={item.avatar?.alt || ''} />
          <AvatarFallback>{(item.name ?? '').slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
      </div>
      <div>
        <Heading as="h6" size={2} html={`${item.comment}`} className="line-clamp-5 min-h-[120px] text-wrap" />
      </div>
    </div>
  )
}

/**
 * MarqueeReview - 垂直滚动的评论轮播组件
 *
 * 用于展示用户评论,支持多列垂直无限滚动效果。
 *
 * @example
 * ```tsx
 * <MarqueeReview
 *   data={{
 *     title: "客户评价",
 *     subtitle: "看看用户怎么说",
 *     items: [
 *       {
 *         name: "张三",
 *         avatar: "/avatar1.jpg",
 *         comment: "非常好用的产品!",
 *         rating: 5
 *       }
 *     ]
 *   }}
 * />
 * ```
 */
function MarqueeReview({ data, className, ...rest }: MarqueeReviewProps) {
  const { title, subtitle, items = [], theme = 'light' } = data

  const chunkItems = _chunk(
    items.length < FIX_ITEMS_LENGTH ? [...items, ...items.slice(FIX_ITEMS_LENGTH - items.length)] : items,
    COUNT
  )
    .slice(0, 3)
    .map(item => [...item, ...item])

  return (
    <section
      {...rest}
      className={cn('text-info-primary flex flex-col items-center justify-center py-3', className, {
        'aiui-dark': theme === 'dark',
      })}
    >
      <Heading as="h1" size={4} html={title} className="laptop:text-center w-full text-left" />
      <Text
        size={2}
        html={subtitle}
        className="tablet:mt-[16px] tablet:text-[14px] laptop:text-center laptop:text-[14px] desktop:mt-[8px] desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] w-full text-left text-[14px]"
      />
      <div
        className={cn(
          'mask-fade-vertical laptop:h-[384px] desktop:h-[512px] lg-desktop:h-[640px] mt-[24px] flex h-[480px] gap-4 overflow-hidden'
        )}
      >
        {chunkItems.map((items, index) => (
          <div
            key={index}
            className={cn('animate-marquee-scroll relative flex h-fit flex-1 flex-col gap-4', {
              'top-10': index === 0 || index === 2,
              'hidden desktop:flex': index === 1,
              'hidden tablet:flex': index === 2,
            })}
          >
            {items.map((item, innerIndex) => (
              <Card key={innerIndex} item={item} />
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}

export default withLayout(MarqueeReview)
